import type { SurveyLookAndFeelSettings, SurveyStyleGradient } from "../types/Survey";
import { darkText } from "./surveyStyleKeys";

export function gradientImage(gradient?: SurveyStyleGradient): string | null {
    if (!gradient) { return null; }
    const to = gradient.direction === "LeftToRight" ? "to right" : "to bottom";
    return `linear-gradient(${to}, ${gradient.startColor} 0%, ${gradient.endColor} 100%)`;
}

// A dark next-button color replaces the light gradient; null means dark keeps the light one.
export function darkGradientImage(styles: SurveyLookAndFeelSettings): string | null {
    if (styles.darkModeNextButtonBackgroundGradient) {
        return gradientImage(styles.darkModeNextButtonBackgroundGradient);
    }
    return styles.darkModeNextButtonBackgroundColor ? "none" : null;
}

// A light :root block plus a .survey-scheme-dark override, so the frame switches by swapping the class on <html>.
export function makeCssVars(styles: SurveyLookAndFeelSettings): string {
    function makeVarIfSet(varName: string, value?: string | null, suffix?: string): string {
        if (value) {
            return ` ${varName}: ${value}${suffix || ""};`;
        }
        return "";
    }

    let lightVars = "";
    lightVars += makeVarIfSet("--title-font-size", styles.titleFontSize, "px");
    lightVars += makeVarIfSet("--title-text-align", styles.titleAlignment);
    lightVars += makeVarIfSet("--title-color", styles.titleColor);
    lightVars += makeVarIfSet("--title-font-weight", styles.titleFontWeight);

    lightVars += makeVarIfSet("--text-font-size", styles.textFontSize, "px");
    lightVars += makeVarIfSet("--text-text-align", styles.textAlignment);
    lightVars += makeVarIfSet("--text-color", styles.textColor);
    lightVars += makeVarIfSet("--text-font-weight", styles.textFontWeight);

    lightVars += makeVarIfSet("--next-button-color", styles.nextButtonTextColor);
    lightVars += makeVarIfSet("--next-button-background-color", styles.nextButtonBackgroundColor);
    lightVars += makeVarIfSet("--next-button-font-weight", styles.nextButtonFontWeight);
    lightVars += makeVarIfSet("--next-button-letter-spacing", styles.nextButtonLetterSpacing);
    lightVars += makeVarIfSet("--next-button-text-transform", styles.nextButtonTextTransform?.toLowerCase());
    if (styles.nextButtonBackgroundGradient) {
        lightVars += makeVarIfSet("--next-button-background-image", gradientImage(styles.nextButtonBackgroundGradient));
    }

    let darkVars = "";
    darkVars += makeVarIfSet("--frame-background", darkText(styles, "backgroundColor"));
    darkVars += makeVarIfSet("--title-color", darkText(styles, "titleColor") || (styles.titleColor ? "var(--frame-text-color)" : undefined));
    darkVars += makeVarIfSet("--text-color", darkText(styles, "textColor") || (styles.textColor ? "var(--frame-text-color)" : undefined));
    darkVars += makeVarIfSet("--next-button-color", darkText(styles, "nextButtonTextColor"));
    darkVars += makeVarIfSet("--next-button-background-color", darkText(styles, "nextButtonBackgroundColor"));
    const darkImage = darkGradientImage(styles);
    if (darkImage !== "none" || styles.nextButtonBackgroundGradient) {
        darkVars += makeVarIfSet("--next-button-background-image", darkImage);
    }

    let css = "";
    if (lightVars.length) {
        css += `:root {${lightVars} }`;
    }
    if (darkVars.length) {
        css += `${css.length ? " " : ""}:root.survey-scheme-dark {${darkVars} }`;
    }
    return css;
}
